import { isValidNumber } from './is-valid-number';

export const DEFAULT_ROOT_MARGIN: string = '0px';
export const DEFAULT_THRESHOLD: number = 0.01;
export const DEFAULT_FALLBACK_SCROLL_POLL_PX: number = 100;
export const DEFAULT_ROOT_ELEMENT = null;

export class ViewportObserverOptions {
  /** the element to watch */
  element: HTMLElement;

  /** function to be called when the element enters the viewport */
  viewportEnterFn: () => void;

  /** function to be called when the element leaves the viewport */
  viewportLeaveFn?: () => void;

  /** viewport intersection offset */
  rootMargin?: string = DEFAULT_ROOT_MARGIN;

  /** viewport intersection threshold. 0.01 means 1% of the element is in viewport */
  threshold?: number = DEFAULT_THRESHOLD;

  /** the Element or Document whose bounds are used as the bounding box when testing for intersection */
  root?: HTMLElement = DEFAULT_ROOT_ELEMENT;
}

/**
 * @name ViewportObserver
 *
 * @description Adds an intersection observer (or browser compatible fallback) and triggers callsbacks, whenever an observed element enters or leaves the viewport
 *
 * @example
 * import { ViewportObserver } from '@workbench/core';
 *
 * // create viewport observer
 * const observer = new ViewportObserver({
 *   element: this.el,
 *   viewportEnterFn: () => this.viewportEnter(),
 *   viewportLeaveFn: () => this.viewportLeave(),
 * });
 *
 * // when done, disconnect viewport observer
 * this.viewportObserver.disconnect();
 */
export class ViewportObserver extends ViewportObserverOptions {
  private intersectionObserver: IntersectionObserver;

  constructor(options: ViewportObserverOptions) {
    super();
    this.initialize(options);
  }

  private initialize(options: ViewportObserverOptions): void {
    this.element = options.element;
    this.viewportEnterFn = options.viewportEnterFn;

    if (options.root) {
      this.root = options.root;
    }

    if (options.viewportLeaveFn) {
      this.viewportLeaveFn = options.viewportLeaveFn;
    }

    if (options.rootMargin) {
      this.rootMargin = options.rootMargin;
    }

    if (isValidNumber(options.threshold)) {
      this.threshold = options.threshold;
    }

    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.viewportEnterFn();
        } else if (this.viewportLeaveFn) {
          this.viewportLeaveFn();
        }
      },
      {
        root: this.root,
        rootMargin: this.rootMargin,
        threshold: this.threshold,
      },
    );
    this.intersectionObserver.observe(this.element);
  }

  /** disconnects existing intersection observer if existent */
  public disconnect(): void {
    this.intersectionObserver.disconnect();
  }
}
